import { createContext, useContext, type ReactNode } from 'react';
import { resolveCopySlot, type CopyVars } from '@shared/copy-slots';

/**
 * How a component asks for words.
 *
 *     const t = useCopy();
 *     <h1>{t('home.hero.title')}</h1>
 *
 * The provider sits in the public layout and holds this city's name and its
 * overrides, so no component ever receives a city as a prop just to say its
 * name — and no component contains a sentence only one city could say.
 */

interface CopyContextValue {
  vars: CopyVars;
  overrides: Readonly<Record<string, string>>;
}

const FALLBACK: CopyContextValue = {
  // Neutral, not Tampa. If the provider is ever missing, the page should read
  // as generic rather than confidently as the wrong city.
  vars: { city: 'your city', site: 'Networking Calendar', areas: '' },
  overrides: {},
};

const CopyContext = createContext<CopyContextValue>(FALLBACK);

export function CopyProvider({
  vars,
  overrides = {},
  children,
}: {
  vars: CopyVars;
  overrides?: Readonly<Record<string, string>>;
  children: ReactNode;
}) {
  return <CopyContext.Provider value={{ vars, overrides }}>{children}</CopyContext.Provider>;
}

export function useCopy(): (id: string) => string {
  const { vars, overrides } = useContext(CopyContext);
  return (id: string) => resolveCopySlot(id, vars, overrides);
}

/**
 * Render a slot whose default marks one word with *asterisks* — the hero title
 * does this so the accent colour lands on a word the CITY chose, not on a word
 * the component hard-codes.
 */
export function Accented({ text }: { text: string }) {
  const parts = text.split(/\*([^*]+)\*/);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <span key={i} className="accent">
            {part}
          </span>
        ) : (
          part
        )
      )}
    </>
  );
}
